import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import dynamic from 'next/dynamic';
import InfiniteScroll from 'react-infinite-scroller';

// Types
import type { ThemeSearchRequest, WThemeGroupSearchResultsView } from '@zola/svc-web-api-ts-client';
import type { UserContext } from '@zola-helpers/client/dist/types/@types';

// Hooks
import { useForceUpdateOnWindowResize } from '@zola/zola-ui/src/hooks/useForceUpdateOnWindowResize';

// Helpers + Utils
import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';
import {
  flatMapFormFilters,
  PLPFilterOptions,
  updateQueryParams,
} from 'pages/WebsiteDesignsPLPSSR/helpers';
import { getDesktopNavHeight, getWebsiteSecondaryNavType } from 'components/common/header/helpers';
import { trackProductListFiltered } from '@zola-helpers/client/dist/es/tracking/productListFiltered';
import { SEGMENTS } from 'pages/WebsiteDesignsPLPV2/utils';
import { useIsPostAuthNav3Desktop } from 'util/isPostAuthNav3Active';

// Redux
import {
  clearThemes,
  updateFilters,
  updateSort,
} from 'pages/WebsiteDesignsPLPSSR/reducer/websitePlpActions';
import { fetchThemesWebsiteV2 } from 'pages/WebsiteDesignsPLPSSR/reducer/websitePlpActionsFetch';
import { getUserIsGuest } from 'selectors/user/userSelectors';
import { useAppSelector } from 'reducers/useAppSelector';
import { useAppDispatch } from 'reducers/useAppDispatch';

// Components
import PLPCallout from 'pages/WebsiteDesignsPLPV2/components/PLPCallout';
import PLPControlsBar from 'pages/WebsiteDesignsPLPV2/components/PLPControlsBar';
import PLPCurrentFilters from 'pages/WebsiteDesignsPLPV2/components/PLPCurrentFilters';
import PLPHeader from 'pages/WebsiteDesignsPLPV2/components/PLPHeader';
import PLPMobileBar from 'pages/WebsiteDesignsPLPV2/components/PLPMobileBar';
import PLPMobileControls from 'pages/WebsiteDesignsPLPV2/components/PLPMobileControls';
import PLPThemeGroup from 'pages/WebsiteDesignsPLPV2/components/PLPThemeGroup';
import WebsiteDesignsPLPMeta from 'pages/WebsiteDesignsPLPSSR/WebsiteDesignsPLPMeta';
import PLPNoResults from 'pages/WebsiteDesignsPLPV2/components/PLPNoResults';

// styles
import { ThemeGroupList } from 'pages/WebsiteDesignsPLPV2/components/PLPThemeGroup/PLPThemeGroup.styles';
import {
  MarketingModuleContainer,
  ThemePreviewTile,
} from 'pages/WebsiteDesignsPLPSSR/ThemeGroup/ThemeGroup.styles';
import {
  PaddedContainer,
  ControlsContainer,
  Page,
  StyledBreadcrumbs,
  V1MarketingModuleContainer,
  PLPContentWrapper,
} from './WebsiteDesignsPLPV2.styles';

const LoadingHeightContext = React.createContext({ height: '172px' });

const UnifiedNav = dynamic(() => import('components/common/header/UnifiedNav'), {
  loading: () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const value = useContext(LoadingHeightContext);
    return <div style={value} />;
  },
  ssr: false,
});

const PreauthFooterV3 = dynamic(
  () => import('@zola/zola-ui/src/components/PreAuthV3/PreauthFooterV3'),
  { ssr: false }
);

const Footer = dynamic(
  () => import('@zola/zola-ui/src/components/Footer').then(module => module.Footer),
  { ssr: false }
);

const MinimalLoader = dynamic(() => import('components/common/ui/MinimalLoader'), {
  ssr: false,
});

export type WebsiteDesignsPLPV2Props = {
  isDesktop?: boolean;
};

/**
 * Shared component for both the pre-auth and post-auth website designs plps. This is V2, which handles both single and multi-page websites
 * NOTE: the post-auth PLP different from post-auth Change Design page. This PLP is only shown, post-auth, when the user has not chosen a website design yet
 */
const WebsiteDesignsPLPV2: React.FC<WebsiteDesignsPLPV2Props> = ({ isDesktop }) => {
  const dispatch = useAppDispatch();

  const isGuest: boolean = useAppSelector(getUserIsGuest);
  const userContext = useAppSelector(state => state.user.userContext) as UserContext;
  const themeGroups = useAppSelector(
    state => (state.websitePlp.theme_groups as unknown) as WThemeGroupSearchResultsView[]
  );
  const currentSort = useAppSelector(state => state.websitePlp.applied_sort);
  const currentFilters = useAppSelector(state => state.websitePlp.applied_filters);
  const currentlyFetchingThemes = useAppSelector(state => state.websitePlp.busy);
  const hasMore = useAppSelector(state => state.websitePlp.hasMore);

  const [offset, setOffset] = useState(12);
  const [layoutType, setLayoutType] = useState<string | SEGMENTS>(SEGMENTS.multipage);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [stickyGridControls, setStickyGridControls] = useState(false);
  const [refContainerBottom, setRefContainerBottom] = useState<number>();
  const isPostAuthNav3 = useIsPostAuthNav3Desktop(userContext);

  const ref = useRef<HTMLDivElement>(null);

  const themeList = themeGroups?.map((themeGroup: WThemeGroupSearchResultsView) => {
    return (
      <PLPThemeGroup
        key={themeGroup.key}
        themeGroup={themeGroup}
        layoutType={layoutType}
        isPostAuthNav3={isPostAuthNav3}
      />
    );
  });

  if (isGuest) {
    if (themeList && themeList?.length >= 1) {
      themeList.splice(0, 0, <PLPCallout calloutType="getStarted" key="PLPCallout--getStarted" />);
    }
    if (themeList && themeList?.length > 6) {
      themeList.splice(7, 0, <PLPCallout calloutType="customize" key="PLPCallout--customize" />);
    }
    if (themeList && themeList?.length > 7) {
      themeList.splice(
        8,
        0,
        <PLPCallout calloutType="startWebsite" key="PLPCallout--startWebsite" />
      );
    }
  }

  if (!isGuest) {
    if (themeList && themeList?.length > 6) {
      themeList.splice(
        7,
        0,
        <V1MarketingModuleContainer key="websiteDesignPLPMarketingTile" data-plpitem>
          <MarketingModuleContainer>
            <a
              href="/wedding-planning/save-the-date/shop"
              aria-label="Match your website to your save the dates and invites: see all designs"
            >
              <ThemePreviewTile />
            </a>
          </MarketingModuleContainer>
        </V1MarketingModuleContainer>
      );
    }
  }

  const handleToggleMobileMenu = () => {
    setMobileMenuVisible(!mobileMenuVisible);
  };

  const getMoreThemes = (): void => {
    if (currentlyFetchingThemes) return;
    setOffset(offset + 12);
    dispatch(
      fetchThemesWebsiteV2({
        ...currentFilters,
        sort_key: currentSort,
        offset,
        limit: 12,
        layout_types: [layoutType],
      })
    ).catch(() => undefined);
  };

  const onUpdateFilter = (filters: PLPFilterOptions): void => {
    dispatch(updateFilters(filters));
    updateQueryParams(filters, currentSort);
    dispatch(clearThemes());
    setOffset(0);
    dispatch(
      fetchThemesWebsiteV2({
        ...filters,
        sort_key: currentSort,
        offset: 0,
        limit: 11,
        layout_types: [layoutType],
      })
    )
      .then(() => {
        setOffset(11);
      })
      .catch(() => undefined);
    trackProductListFiltered({
      category: 'Website',
      filters: flatMapFormFilters(filters),
      sorts: [],
    });
  };

  const onSort = (sort: string | number | null) => {
    if (!sort) return;
    dispatch(clearThemes());
    setOffset(0);
    dispatch(updateSort((sort as unknown) as ThemeSearchRequest.SortKeyEnum));
    updateQueryParams(currentFilters, (sort as unknown) as ThemeSearchRequest.SortKeyEnum);
    dispatch(
      fetchThemesWebsiteV2({
        ...currentFilters,
        sort_key: (sort as unknown) as ThemeSearchRequest.SortKeyEnum,
        offset: 0,
        limit: 11,
        layout_types: [layoutType],
      })
    )
      .then(() => {
        setOffset(11);
      })
      .catch(() => undefined);
  };

  const onToggleLayout = (updatedLayout: string | SEGMENTS) => {
    dispatch(clearThemes());
    setOffset(0);
    dispatch(
      fetchThemesWebsiteV2({
        ...currentFilters,
        sort_key: currentSort,
        offset: 0,
        limit: 11,
        layout_types: [updatedLayout],
      })
    )
      .then(() => {
        setOffset(11);
      })
      .catch(() => undefined);
  };

  const trackPLPScrolling = useCallback(() => {
    if (typeof window !== 'undefined') {
      const currentScroll =
        window.pageYOffset + (!isDesktop ? 60 : getDesktopNavHeight(isGuest, isGuest));
      if (refContainerBottom && currentScroll >= refContainerBottom) {
        setStickyGridControls(true);
      } else {
        setStickyGridControls(false);
      }
    }
  }, [isDesktop, isGuest, refContainerBottom]);

  const renderThemeList = (): JSX.Element | null => {
    if (themeGroups && themeGroups.length > 0) {
      return (
        <InfiniteScroll
          loadMore={getMoreThemes}
          hasMore={hasMore && offset > 0}
          initialLoad={false}
          threshold={20}
        >
          <ThemeGroupList data-testid="WebsiteDesignsPLPV2__Grid" isPostAuthNav3={isPostAuthNav3}>
            {themeList}
          </ThemeGroupList>
        </InfiniteScroll>
      );
    }
    if (!currentlyFetchingThemes) {
      return (
        <PLPNoResults
          clearFilters={() => onUpdateFilter({ colors: [], special_features: [], motifs: [] })}
        />
      );
    }
    return null;
  };

  useEffect(() => {
    if (ref && ref.current) {
      setRefContainerBottom(ref?.current?.getBoundingClientRect().bottom);
    }
  }, [ref]);

  useEffect(() => {
    document.removeEventListener('scroll', trackPLPScrolling, false);
    if (refContainerBottom && themeGroups && themeGroups.length >= 8) {
      document.addEventListener('scroll', trackPLPScrolling, false);
    }
  }, [refContainerBottom, themeGroups, trackPLPScrolling]);

  useForceUpdateOnWindowResize();
  const navHeight = !isDesktop ? 60 : getDesktopNavHeight(isGuest, isGuest);

  return (
    <Page data-testid="WebsiteDesignsPLPV2">
      <WebsiteDesignsPLPMeta />
      <LoadingHeightContext.Provider value={{ height: `${navHeight}px` }}>
        <UnifiedNav
          activePrimaryLink={PrimaryNavIdentifier.WEBSITE}
          secondaryType={getWebsiteSecondaryNavType(userContext)}
          initialHeight={navHeight}
        />
      </LoadingHeightContext.Provider>
      <PLPContentWrapper isPostAuthNav3={isPostAuthNav3} data-testid="PLPV2Content">
        <PaddedContainer isPostAuthNav3={isPostAuthNav3} ref={ref}>
          <StyledBreadcrumbs
            currentSelectionLabel="Designs"
            linkItems={[
              {
                label: 'Website',
                to: userContext.is_guest
                  ? '/wedding-planning/website'
                  : '/wedding/manage/website/manage',
              },
            ]}
          />
          <PLPHeader />
          <ControlsContainer
            stickyGridControls={stickyGridControls}
            isGuest={isGuest}
            isPostAuthNav3={isPostAuthNav3}
          >
            {/* Desktop Controls */}
            <PLPControlsBar
              onSort={onSort}
              onToggleLayout={onToggleLayout}
              onUpdateFilter={onUpdateFilter}
              setLayoutType={setLayoutType}
            />
            {/* Desktop Controls */}
            {/* Tablet/Mobile Controls */}
            <PLPMobileBar
              onToggleLayout={onToggleLayout}
              setLayoutType={setLayoutType}
              handleToggleMobileMenu={handleToggleMobileMenu}
            />
            {/* Tablet/Mobile Controls */}
            <PLPCurrentFilters onUpdateFilter={onUpdateFilter} />
          </ControlsContainer>
        </PaddedContainer>
        {renderThemeList()}
        {currentlyFetchingThemes && <MinimalLoader />}
      </PLPContentWrapper>
      {isGuest ? <PreauthFooterV3 isGuest={isGuest} /> : <Footer />}
      <PLPMobileControls
        layoutType={layoutType}
        handleToggleMobileMenu={handleToggleMobileMenu}
        mobileMenuVisible={mobileMenuVisible}
        onSort={onSort}
        onToggleLayout={onToggleLayout}
        onUpdateFilter={onUpdateFilter}
        setLayoutType={setLayoutType}
      />
    </Page>
  );
};

export default WebsiteDesignsPLPV2;
